import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatErr } from "../../../../lib/api";
import { Input } from "../../../../components/ui/input";
import { Button } from "../../../../components/ui/button";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { ActionLink, ActionsCell, ConfirmDeleteDialog, EmptyState, Field, SectionCard, StatusPill } from "./shared";
import { ACCOUNTING_BASE } from "./accountingNav";

const fyBlank = { code: "", startDate: "", endDate: "", setCurrent: false };

// Phase 5 correction (item 11 - Financial Year Display): the Society's
// standard FY naming is the short "2026-27" form, not "2026-2027" - shown
// consistently in voucher numbers (e.g. PMT/2026-27/000001) and everywhere
// else the FY appears. This only auto-SUGGESTS that short form from the
// chosen dates (still editable, same pattern as the Bank Ledger Name
// suggestion) - it never rewrites an existing Financial Year's code, since
// codes are immutable once a Financial Year is created and posted vouchers
// may already reference it.
function suggestFinancialYearCode(startDate) {
  if (!startDate) return "";
  const startYear = Number(String(startDate).slice(0, 4));
  if (!Number.isFinite(startYear)) return "";
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
}

const FY_CODE_PATTERN = /^\d{4}-\d{2}$/;

// Financial Years now read/write the real Phase 3 engine
// (/society/accounting/engine/financial-years) instead of the old single
// "financialYear text field" setup document. Dates are intentionally never
// editable after creation - only status (open/locked) and "set current" -
// so a posted voucher's financial year can never be invalidated by an edit.
export default function FinancialYearsPage() {
  const [years, setYears] = useState([]);
  const [form, setForm] = useState(fyBlank);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/society/accounting/engine/financial-years").catch(() => ({ data: [] }));
      setYears(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(formatErr(err, "Could not load financial years"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createYear = async () => {
    if (!form.code.trim() || !form.startDate || !form.endDate) { toast.error("Code, start date and end date are required"); return; }
    try {
      setBusy(true);
      await api.post("/society/accounting/engine/financial-years", form);
      toast.success("Financial year created");
      setForm(fyBlank);
      setShowAddForm(false);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not create financial year"));
    } finally {
      setBusy(false);
    }
  };

  const setCurrent = async (fy) => {
    try {
      await api.patch(`/society/accounting/engine/financial-years/${fy.id}`, { setCurrent: true });
      toast.success(`${fy.code} set as current financial year`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not set current financial year"));
    }
  };

  // Phase 9 (item 39): FY status changes are sensitive enough to warrant a
  // confirmation before firing - the backend's own before/after audit log
  // entry (society_audit_logs, entityType "financial_year") already records
  // every change permanently, so this is a lightweight confirm rather than
  // the fuller reason-required dialog used for reconciliation/period-lock/
  // auditor actions (which the backend requires an explicit reason for).
  const changeStatus = async (fy, status, confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.patch(`/society/accounting/engine/financial-years/${fy.id}`, { status });
      toast.success(`${fy.code} status set to ${status}`);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not update financial year status"));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await api.delete(`/society/accounting/engine/financial-years/${deleteTarget.id}`);
      toast.success("Financial year deleted");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      toast.error(formatErr(err, "This financial year cannot be deleted because accounting vouchers or entries fall within its date range."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-extrabold text-[#111827]">Financial Years</h2>
          <p className="mt-1 text-sm text-[#6B7280]">Used across ledgers, vouchers and reports. Dates can never be changed after creation to protect posted vouchers.</p>
        </div>
        {!showAddForm && (
          <button type="button" onClick={() => setShowAddForm(true)} className="rounded-sm bg-[#111827] px-4 py-2 text-sm font-semibold text-white hover:bg-[#374151]">
            + Add financial year
          </button>
        )}
      </div>

      {showAddForm && (
        <SectionCard title="Add financial year">
          <div className="grid gap-3 md:grid-cols-4">
            <Field label="Code">
              <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="2026-27" />
              {form.code && !FY_CODE_PATTERN.test(form.code) && (
                <p className="mt-1 text-[11px] text-amber-700">Standard format is "YYYY-YY" (e.g. 2026-27) - used consistently in voucher numbers across Accounting.</p>
              )}
            </Field>
            <Field label="Start date">
              <Input
                type="date"
                value={form.startDate}
                onChange={(e) => {
                  const startDate = e.target.value;
                  setForm((f) => ({ ...f, startDate, code: f.code || suggestFinancialYearCode(startDate) }));
                }}
              />
            </Field>
            <Field label="End date"><Input type="date" value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} /></Field>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-xs text-[#374151]">
                <input type="checkbox" className="h-3.5 w-3.5 rounded-sm border-[#D1D5DB]" checked={form.setCurrent} onChange={(e) => setForm((f) => ({ ...f, setCurrent: e.target.checked }))} />
                Set as current
              </label>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button disabled={busy} onClick={createYear} className="bg-[#111827] text-white hover:bg-[#374151]"><Plus className="mr-2 h-4 w-4" /> Add financial year</Button>
            <Button type="button" variant="outline" disabled={busy} onClick={() => { setShowAddForm(false); setForm(fyBlank); }}>Cancel</Button>
          </div>
        </SectionCard>
      )}

      <SectionCard>
        {loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : years.length === 0 ? (
          <EmptyState>No financial years yet. Add one above before posting vouchers.</EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-left text-xs uppercase tracking-[0.12em] text-[#6B7280]">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">Start date</th>
                  <th className="py-2 pr-3">End date</th>
                  <th className="py-2 pr-3">Current</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {years.map((fy) => (
                  <tr key={fy.id} className="border-b border-[#F3F4F6]">
                    <td className="py-2 pr-3 font-semibold text-[#111827]">{fy.code}</td>
                    <td className="py-2 pr-3">{fy.startDate}</td>
                    <td className="py-2 pr-3">{fy.endDate}</td>
                    <td className="py-2 pr-3">{fy.isCurrent ? <span className="rounded-sm bg-[#111827] px-2 py-0.5 text-xs font-semibold text-white">Current</span> : "-"}</td>
                    <td className="py-2 pr-3">
                      {fy.status === "locked" ? <StatusPill active={false} falseLabel="Locked" /> : fy.status === "closing" ? <span className="rounded-sm bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Closing</span> : <StatusPill active trueLabel="Open" />}
                    </td>
                    <td className="py-2 pr-3">
                      <ActionsCell>
                        <ActionLink to={`${ACCOUNTING_BASE}/financial-years/${fy.id}`}>View</ActionLink>
                        <span className="text-[#D1D5DB]">|</span>
                        {!fy.isCurrent && <><ActionLink onClick={() => setCurrent(fy)}>Set Current</ActionLink><span className="text-[#D1D5DB]">|</span></>}
                        {fy.status === "open" && <><ActionLink onClick={() => changeStatus(fy, "closing", `Mark ${fy.code} as Closing (year-end review in progress)? This does not block posting yet.`)}>Mark Closing</ActionLink><span className="text-[#D1D5DB]">|</span></>}
                        {fy.status === "closing" && <><ActionLink onClick={() => changeStatus(fy, "open", `Reopen ${fy.code} back to Open?`)}>Reopen</ActionLink><span className="text-[#D1D5DB]">|</span></>}
                        <ActionLink onClick={() => changeStatus(fy, fy.status === "locked" ? "open" : "locked", fy.status === "locked" ? `Reopen (unlock) ${fy.code}? Normal users will be able to post vouchers in this year again.` : `Lock ${fy.code}? This blocks all posting/editing/cancelling for this Financial Year.`)}>{fy.status === "locked" ? "Reopen (Unlock)" : "Lock"}</ActionLink>
                        {fy.canDelete && (
                          <>
                            <span className="text-[#D1D5DB]">|</span>
                            <ActionLink tone="danger" onClick={() => setDeleteTarget(fy)}>Delete</ActionLink>
                          </>
                        )}
                      </ActionsCell>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete financial year?"
        identity={deleteTarget?.code}
        safeMessage="This financial year has no accounting vouchers or entries and can be permanently deleted."
        busy={deleting}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
